/**
 * The DOCE customer report: what its columns are, and what it looks like on paper.
 *
 * Built in the browser from the rows the API returns, rather than rendered server-side, for the same
 * reason the order receipt is (`lib/store/receipt.ts`): this is printed, every browser turns a print
 * dialog into a PDF, and a generated PDF would mean a layout library in the Lambda bundle and a second
 * place for the report to be formatted. The exports are built here too, so a round of "CSV, then XLSX,
 * then print" is one fetch and three local renders rather than three passes over Stripe.
 */

import type { Column } from './csv';

/** One customer, as `GET /admin/reports/doce` returns them. Mirrors `lib/domain/doceReport.ts`. */
export interface DoceRow {
  accountId: string | null;
  name: string;
  stripeCustomerId: string;
  email: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  planName: string;
  website: string;
  billing: string;
  status: string;
  source: 'account' | 'stripe';
  internal: boolean;
  internalReason: string | null;
}

export interface DoceReportResponse {
  generatedOn: string;
  rows: DoceRow[];
  counts: { total: number; internal: number; withoutAccount: number; withoutWebsite: number; withoutAddress: number };
}

const esc = (s: string): string =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The URL as a person would write it — no scheme, no trailing slash — so the column stays narrow. */
export const prettyUrl = (url: string): string => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

/** The address on one line, blanks dropped so an incomplete record does not print as gaps. */
export function oneLineAddress(r: DoceRow): string {
  const street = [r.line1, r.line2].filter(Boolean).join(', ');
  const cityLine = [r.city, r.state].filter(Boolean).join(', ');
  return [street, cityLine, r.postalCode].filter(Boolean).join(' · ') || '—';
}

/**
 * The spreadsheet's columns, with the address SPLIT.
 *
 * The printed report runs the address together on one line because that is how it reads; a spreadsheet is
 * opened to sort by state or to feed a mail merge, and neither works on a single joined string. The two
 * views of the same row genuinely want different shapes, so they get them.
 */
export const DOCE_EXPORT_COLUMNS: readonly Column<DoceRow>[] = [
  { header: 'Customer', value: (r) => r.name },
  { header: 'Email', value: (r) => r.email },
  { header: 'Address 1', value: (r) => r.line1 },
  { header: 'Address 2', value: (r) => r.line2 },
  { header: 'City', value: (r) => r.city },
  { header: 'State', value: (r) => r.state },
  { header: 'ZIP', value: (r) => r.postalCode },
  { header: 'Website', value: (r) => r.website },
  { header: 'Plan', value: (r) => r.planName },
  { header: 'Billing', value: (r) => r.billing },
  { header: 'Subscription status', value: (r) => r.status },
  // Both identifiers, because the usual reason to export this is to go and look something up.
  { header: 'Account ID', value: (r) => r.accountId ?? '' },
  { header: 'Stripe customer', value: (r) => r.stripeCustomerId },
  { header: 'Address source', value: (r) => (r.source === 'account' ? 'EFTCreator account' : 'Stripe billing') },
];

/** `EFTCreator-DOCE-customers-20261006`. Dated, because these get filed and compared. */
export function reportFilename(generatedOn: string): string {
  const ymd = new Date(generatedOn);
  const stamp = Number.isNaN(ymd.getTime())
    ? 'undated'
    : `${ymd.getFullYear()}${String(ymd.getMonth() + 1).padStart(2, '0')}${String(ymd.getDate()).padStart(2, '0')}`;
  return `EFTCreator-DOCE-customers-${stamp}`;
}

/** Long form, for the corner of the printed page. */
export function printedDate(generatedOn: string): string {
  const at = new Date(generatedOn);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * The printable document.
 *
 * Landscape letter, one row per customer, and `break-inside: avoid` on every row — half a customer across
 * a page break is unusable in the field, which is where this gets read.
 */
export function doceReportHtml(rows: readonly DoceRow[], generatedOn: string): string {
  const body = rows
    .map(
      (r, i) => `<tr>
      <td class="n">${i + 1}</td>
      <td><div class="cust">${esc(r.name)}</div>${r.email ? `<div class="sub">${esc(r.email)}</div>` : ''}</td>
      <td>${esc(oneLineAddress(r))}${
        r.source === 'stripe' ? '<div class="sub">billing address on file — no EFTCreator account yet</div>' : ''
      }</td>
      ${/* A dash, not a blank: "we looked and found nothing" reads differently from an empty cell. */ ''}
      <td>${r.website ? `<a href="${esc(r.website)}">${esc(prettyUrl(r.website))}</a>` : '<span class="none">—</span>'}</td>
      <td>${esc(r.planName)}<div class="sub">${esc(r.billing)}</div></td>
    </tr>`,
    )
    .join('');

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(reportFilename(generatedOn))}</title>
<style>
  @page { size: letter landscape; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 10.5pt; margin: 0; padding: 14mm; }
  .head { border-bottom: 3px solid #55B848; padding-bottom: 10px; margin-bottom: 16px;
          display: flex; justify-content: space-between; align-items: flex-end; }
  .brand { font-size: 18pt; font-weight: bold; letter-spacing: -0.5px; }
  .brand span { color: #55B848; }
  .title { font-size: 12pt; margin-top: 2px; }
  .meta { text-align: right; font-size: 9pt; color: #777; line-height: 1.5; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 8.5pt; text-transform: uppercase; letter-spacing: .5px; color: #777;
       border-bottom: 1.5px solid #ddd; padding: 6px 8px; }
  td { padding: 7px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
  td.n { color: #aaa; width: 28px; }
  .cust { font-weight: bold; }
  .sub { font-size: 8.5pt; color: #888; margin-top: 1px; }
  .none { color: #bbb; }
  a { color: #2c7a24; text-decoration: none; }
  /* Never split a customer across a page — half a row is unusable in the field. */
  tr { break-inside: avoid; }
  .foot { margin-top: 22px; padding-top: 10px; border-top: 1px solid #eee; font-size: 8.5pt; color: #999; }
  @media print { body { padding: 0; } }
</style></head><body>
  <div class="head">
    <div>
      <div class="brand">EFT<span>Creator</span></div>
      <div class="title">Active FBI Departmental Order (DOCE) customers</div>
    </div>
    <div class="meta">
      ${esc(printedDate(generatedOn))}<br>
      ${rows.length} customer${rows.length === 1 ? '' : 's'}
    </div>
  </div>

  <table>
    <thead><tr><th></th><th>Customer</th><th>Physical address</th><th>Website</th><th>Plan</th></tr></thead>
    <tbody>${body || '<tr><td colspan="5">No active DOCE subscriptions found.</td></tr>'}</tbody>
  </table>

  <div class="foot">
    Subscription status is read live from Stripe; the address is the physical address on the customer's
    EFTCreator account. Lakota Software Solutions, Inc. — internal.
  </div>
</body></html>`;
}
